import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { alibabaApi, type AlibabaProductProfile } from '@/api/alibaba';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ExternalLink, ArrowLeft } from 'lucide-react';
import { useState } from 'react';

// -- Image lightbox --
function ImageLightbox({ src, onClose }: { src: string; onClose: () => void }) {
  return (
    <div
      className='fixed inset-0 z-50 bg-black/80 flex items-center justify-center'
      onClick={onClose}
    >
      <img
        src={src}
        alt='product'
        className='max-h-[90vh] max-w-[90vw] object-contain rounded'
        onClick={e => e.stopPropagation()}
      />
    </div>
  );
}

// -- Helper: format price --
function formatPrice(p: AlibabaProductProfile): string {
  if (p.price_min == null && p.price_max == null) return '---';
  const cur = p.price_currency ?? 'USD';
  if (p.price_min != null && p.price_max != null && p.price_min !== p.price_max)
    return cur + ' ' + p.price_min + ' ~ ' + p.price_max;
  return cur + ' ' + (p.price_min ?? p.price_max);
}

// -- Helper: format date --
function fmtDate(iso: string | null): string {
  if (!iso) return '---';
  return new Date(iso).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' });
}

export default function ProductProfile() {
  const { task_id } = useParams<{ task_id: string }>();
  const [lightbox, setLightbox] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['product-profile', task_id],
    queryFn: () => alibabaApi.getProductProfile(task_id!),
    enabled: !!task_id,
  });

  const p: AlibabaProductProfile | undefined = data?.data;

  if (isLoading) {
    return (
      <div className='p-6 space-y-4 max-w-5xl mx-auto'>
        <Skeleton className='h-8 w-64' />
        <Skeleton className='h-40 w-full' />
        <Skeleton className='h-60 w-full' />
      </div>
    );
  }

  if (isError || !p) {
    return (
      <div className='p-6 max-w-5xl mx-auto'>
        <Link to='/' className='flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-foreground'>
          <ArrowLeft className='w-4 h-4' /> 返回儀表板
        </Link>
        <Card className='border-destructive'>
          <CardContent className='p-6 text-destructive'>
            {error instanceof Error ? error.message : '找不到商品'}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Prefer ShareMyImage CDN images, fallback to iframe images
  const images: string[] =
    (p.smi_image_urls && p.smi_image_urls.length > 0)
      ? p.smi_image_urls
      : (p.iframe_image_urls ?? []);

  return (
    <div className='p-4 md:p-6 space-y-4 max-w-5xl mx-auto'>
      {lightbox && <ImageLightbox src={lightbox} onClose={() => setLightbox(null)} />}

      {/* Header */}
      <div className='flex items-start justify-between gap-3'>
        <div className='flex-1 min-w-0'>
          <Link
            to='/'
            className='flex items-center gap-1 text-xs text-muted-foreground mb-1 hover:text-foreground w-fit'
          >
            <ArrowLeft className='w-3 h-3' /> 返回儀表板
          </Link>
          <h1 className='text-xl font-semibold leading-snug'>
            {p.product_name ?? 'Product ' + p.task_id}
          </h1>
          <p className='text-xs text-muted-foreground mt-0.5 break-all'>{p.product_url}</p>
        </div>
        {p.product_url && (
          <a
            href={p.product_url}
            target='_blank'
            rel='noreferrer'
            className='flex items-center gap-1 text-xs text-blue-400 hover:underline shrink-0'
          >
            阿里巴巴 <ExternalLink className='w-3 h-3' />
          </a>
        )}
      </div>

      {/* Basic Info */}
      <Card>
        <CardHeader className='pb-2'>
          <CardTitle className='text-sm'>基本資訊 Basic Info</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className='grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-sm'>
            <div>
              <dt className='text-muted-foreground text-xs'>公司 Company</dt>
              <dd className='font-medium'>{p.company_name ?? '---'}</dd>
            </div>
            <div>
              <dt className='text-muted-foreground text-xs'>價格 Price</dt>
              <dd className='font-medium'>{formatPrice(p)}</dd>
            </div>
            <div>
              <dt className='text-muted-foreground text-xs'>SKU</dt>
              <dd className='font-medium'>{p.sku ?? '---'}</dd>
            </div>
            <div>
              <dt className='text-muted-foreground text-xs'>MOQ 最低訂量</dt>
              <dd className='font-medium'>{p.moq_quantity != null ? p.moq_quantity + ' 件' : '---'}</dd>
            </div>
            <div>
              <dt className='text-muted-foreground text-xs'>貨期 Lead Time</dt>
              <dd className='font-medium'>
                {p.lead_time_min != null
                  ? p.lead_time_max != null && p.lead_time_max !== p.lead_time_min
                    ? p.lead_time_min + ' ~ ' + p.lead_time_max + ' 天'
                    : p.lead_time_min + ' 天'
                  : '---'}
              </dd>
            </div>
            <div>
              <dt className='text-muted-foreground text-xs'>爬取裝置 Device</dt>
              <dd className='font-medium'>{p.scraped_by ?? '---'}</dd>
            </div>
            <div>
              <dt className='text-muted-foreground text-xs'>最後更新 Updated</dt>
              <dd className='text-xs'>{fmtDate(p.updated_at)}</dd>
            </div>
            <div>
              <dt className='text-muted-foreground text-xs'>爬取 Scraped</dt>
              <dd className='text-xs'>{fmtDate(p.scraped_at)}</dd>
            </div>
            <div>
              <dt className='text-muted-foreground text-xs'>解析 Parsed</dt>
              <dd className='text-xs'>{fmtDate(p.parsed_at)}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Image Gallery */}
      {images.length > 0 && (
        <Card>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm flex items-center gap-2'>
              圖片牆 Images
              <span className='text-xs text-muted-foreground font-normal'>
                {p.smi_image_urls && p.smi_image_urls.length > 0 ? 'ShareMyImage' : 'iframe'}
                {' (' + images.length + ')'}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className='grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2'>
              {images.map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt={'product-' + i}
                  className='w-full aspect-square object-cover rounded cursor-pointer hover:opacity-80 transition-opacity border'
                  onClick={() => setLightbox(url)}
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Variations */}
      {p.variations && p.variations.length > 0 && (
        <Card>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm'>規格變體 Variations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b'>
                    <th className='text-left py-1 pr-4 text-muted-foreground font-medium'>名稱 Name</th>
                    <th className='text-left py-1 text-muted-foreground font-medium'>選項 Options</th>
                  </tr>
                </thead>
                <tbody>
                  {p.variations.map((v: any, i: number) => (
                    <tr key={i} className='border-b last:border-0'>
                      <td className='py-1 pr-4 font-medium whitespace-nowrap'>{v.name ?? v.label ?? String(v)}</td>
                      <td className='py-1 text-muted-foreground'>
                        {Array.isArray(v.options)
                          ? v.options.join(', ')
                          : Array.isArray(v.values)
                          ? v.values.join(', ')
                          : '---'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Price Tiers */}
      {p.price_tiers && p.price_tiers.length > 0 && (
        <Card>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm'>價格梯度 Price Tiers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b'>
                    <th className='text-left py-1 pr-6 text-muted-foreground font-medium'>數量 Qty</th>
                    <th className='text-left py-1 text-muted-foreground font-medium'>單價 Unit Price</th>
                  </tr>
                </thead>
                <tbody>
                  {p.price_tiers.map((t: any, i: number) => (
                    <tr key={i} className='border-b last:border-0'>
                      <td className='py-1 pr-6'>
                        {t.min_qty != null
                          ? t.max_qty != null
                            ? t.min_qty + ' ~ ' + t.max_qty
                            : '>= ' + t.min_qty
                          : t.quantity ?? '---'}
                      </td>
                      <td className='py-1 font-medium'>
                        {t.price ?? t.unit_price ?? '---'}
                        {t.currency ? ' ' + t.currency : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Highlights */}
      {p.highlights && p.highlights.length > 0 && (
        <Card>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm'>亮點 Highlights</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='flex flex-wrap gap-2'>
              {p.highlights.map((h, i) => (
                <Badge key={i} variant='secondary' className='text-xs'>{h}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Full Description */}
      {(p.html_full_markdown || p.iframe_description_text) && (
        <Card>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm'>完整描述 Description</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className='whitespace-pre-wrap text-xs text-muted-foreground leading-relaxed max-h-96 overflow-y-auto'>
              {p.html_full_markdown ?? p.iframe_description_text}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* Related Searches */}
      {p.related_searches && p.related_searches.length > 0 && (
        <Card>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm'>相關搜尋 Related Searches</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='flex flex-wrap gap-2'>
              {p.related_searches.map((s, i) => (
                <Badge key={i} variant='outline' className='text-xs'>{s}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
